import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { useStaffAuth } from '@/features/auth/useStaffAuth';
import { NewStaffDialog } from '@/features/staff/NewStaffDialog';
import {
  classifySetDisabledError,
  useSetStaffDisabled,
  useStaff,
  type StaffMember,
} from '@/features/staff/useStaff';

/**
 * Staff — slice 17w. Modelled on `Tenants.tsx`: the same header-with-action
 * layout, the same table chrome and spacing, the same empty and error states,
 * and the same TanStack-Query mutation pattern.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 SUPER-ADMIN ONLY — AND THE GATE IS HERE, NOT ONLY IN THE NAV.
 * ─────────────────────────────────────────────────────────────────────────────
 * The four routes behind this screen are `requireSuperAdmin`; an `admin_staff`
 * caller gets 403. The nav hides the entry for them (AdminNav reads the same
 * role), but a hidden link is not a closed door — a deep link to `/staff` must
 * not render staff data either. So this component redirects a non-super-admin
 * to the tenants list rather than showing a screen that would 403 on every
 * fetch. Absence over disabled, and absence over a dangling 403.
 *
 * WHY THIS EXISTS: before it, a staff member came into being only through
 * `SEED_STAFF_*` env vars applied by `seed.ts` — a server edit for every hire.
 * This is the portal-driven path.
 */

function StaffRow({
  member,
  onToggle,
  pending,
}: {
  member: StaffMember;
  onToggle: (m: StaffMember) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const disabled = member.disabledAt !== null;
  return (
    <tr className="border-b border-raad-border last:border-b-0">
      <td className="py-3 pr-4">
        <span className="font-medium">{member.name}</span>
      </td>
      <td className="py-3 pr-4 num">{member.phone}</td>
      <td className="py-3 pr-4">{t(`shell.role.${member.role}`)}</td>
      <td className="py-3 pr-4">
        <span
          className={[
            'inline-flex rounded-md border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide',
            disabled ? 'border-raad-error/40 text-raad-error' : 'border-raad-border text-raad-muted',
          ].join(' ')}
        >
          {disabled ? t('staff.state.disabled') : t('staff.state.enabled')}
        </span>
      </td>
      <td className="py-3 pr-4 text-raad-muted">
        {new Date(member.createdAt).toLocaleDateString()}
      </td>
      <td className="py-3">
        <button
          type="button"
          onClick={() => onToggle(member)}
          disabled={pending}
          className={[
            'min-h-9 rounded-lg border px-3 text-[12.5px] font-semibold transition-colors disabled:opacity-50',
            disabled
              ? 'border-raad-primary/40 text-raad-accent hover:bg-raad-primary/10'
              : 'border-raad-border hover:bg-raad-primary/10',
          ].join(' ')}
        >
          {disabled ? t('staff.action.enable') : t('staff.action.disable')}
        </button>
      </td>
    </tr>
  );
}

export function Staff() {
  const { t } = useTranslation();
  const { state } = useStaffAuth();
  const q = useStaff();
  const setDisabled = useSetStaffDisabled();
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  // 🔴 The route-level gate. RequireStaff guarantees we are authenticated, so
  // any non-super-admin here is a genuine `admin_staff` who must not see this.
  if (state.status === 'authenticated' && state.user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  async function toggle(member: StaffMember) {
    setActionError(null);
    setPendingId(member.id);
    try {
      await setDisabled.mutateAsync({
        id: member.id,
        disabled: member.disabledAt === null,
      });
    } catch (e) {
      const kind = classifySetDisabledError(e);
      setActionError(t(`staff.actionError.${kind}`));
    } finally {
      setPendingId(null);
    }
  }

  if (q.isPending) return <p className="text-sm text-raad-muted">{t('staff.loading')}</p>;
  if (q.isError) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('staff.error')}
      </p>
    );
  }

  const data = q.data as Partial<typeof q.data> | undefined;
  const rows = Array.isArray(data?.staff) ? data.staff : null;

  // Defensive: an unexpected shape is an ERROR, never an empty list — the same
  // reasoning `Tenants` records. "No staff" would be a false claim; all we know
  // is the response was unreadable. (In practice the signed-in super-admin is
  // always at least one row, so a truly empty list is itself suspicious.)
  if (rows === null) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('staff.error')}
      </p>
    );
  }

  return (
    <section aria-labelledby="staff-heading">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h1 id="staff-heading" className="font-display text-xl font-bold tracking-tight">
          {t('staff.title')}
        </h1>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="order-last ms-auto min-h-9 rounded-lg bg-raad-primary px-3 text-[12.5px] font-semibold text-white"
        >
          {t('staff.newStaff.newStaff')}
        </button>
        <p className="text-sm text-raad-muted">{t('staff.subtitle')}</p>
      </div>

      {actionError ? (
        <p role="alert" className="mt-3 text-[12.5px] text-raad-error">
          {actionError}
        </p>
      ) : null}

      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">{t('staff.empty')}</p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-panel border border-raad-border bg-raad-panel px-4">
          <table className="w-full min-w-[42rem] text-sm">
            <thead>
              <tr className="border-b border-raad-border text-[11px] uppercase tracking-wide text-raad-muted">
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('staff.col.name')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('staff.col.phone')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('staff.col.role')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('staff.col.state')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('staff.col.created')}
                </th>
                <th scope="col" className="py-2.5 text-left font-semibold">
                  {t('staff.col.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <StaffRow
                  key={m.id}
                  member={m}
                  onToggle={(x) => void toggle(x)}
                  pending={pendingId === m.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating ? <NewStaffDialog onClose={() => setCreating(false)} /> : null}
    </section>
  );
}

export default Staff;
